import React, { useState } from "react";
import {
  ChevronDown, ChevronRight, Trash2, MapPin, Sparkles, AlertTriangle, Settings, MoreVertical, X, Check, Braces,
} from "lucide-react";
import { Spinner } from "../../constants";
import { fieldTypeMeta, confidenceStyle, effectiveConfidence, isEmptyValue, SCHEMA_TYPES, CONFIDENCE_STYLES } from "../studioConst";
import { SX } from "../studioTokens";
import SchemaBuilder from "./SchemaBuilder";

const SOURCE_BADGE = {
  ai_generated: "bg-[#F7EBF5] text-[#893F82]",
  refined:      "bg-[#EEF4FD] text-[#2F5FA8]",
  saved:        "bg-[#F1EEF0] text-[#9A8E97]",
  manual:       "bg-[#F3F3F3] text-[#6B6B6B]",
};

const Label = ({ children, hint }) => (
  <div className="mb-1.5">
    <span className="text-[11px] font-semibold tracking-[0.05em] text-[#52525B] uppercase">{children}</span>
    {hint && <span className="text-[12.5px] text-[#8A8A90] font-normal ml-2 normal-case tracking-normal">{hint}</span>}
  </div>
);

const GroupLabel = ({ icon: Ico, children }) => (
  <div className="flex items-center gap-1.5 mb-3.5">
    <Ico size={13} className="text-[#8A8A90]" />
    <span className="text-[11px] font-semibold tracking-[0.06em] text-[#8A8A90] uppercase">{children}</span>
  </div>
);

/* One before/after row in the refinement review modal. Unchanged values render
   flat rather than as a red/green pair, so the eye goes to what actually moved. */
const DiffRow = ({ label, before, after }) => {
  const changed = (before || "") !== (after || "");
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[11px] font-semibold tracking-[0.05em] text-[#52525B] uppercase">{label}</span>
        {changed && <span className="px-2 py-0.5 rounded-full bg-[#F7EBF5] text-[#893F82] text-[9.5px] font-semibold uppercase tracking-[0.04em]">Updated</span>}
      </div>
      {changed ? (
        <div className="grid gap-[7px]">
          <div className="bg-[#FCEDED] border border-[#F3D6D6] rounded-lg px-3 py-2 text-[12px] text-[#9B3B3B] leading-[1.5] line-through decoration-[#9B3B3B]/40">{before || "—"}</div>
          <div className="bg-[#EAF6EE] border border-[#CFE9D6] rounded-lg px-3 py-2 text-[12px] text-[#2F7D44] leading-[1.5]">{after || "—"}</div>
        </div>
      ) : (
        <div className="text-[12px] text-[#8A8A90] leading-[1.5]">{after || "—"} <span className="italic">· unchanged</span></div>
      )}
    </div>
  );
};

const FieldEditor = ({
  index, field, defaultOpen,
  // review mode (post-generate)
  checkbox, checked, onToggle, confidence, sample,
  // edit mode
  context, onRename, onChangeType, onDelete, onToggleEnable, onLocate, onChangeInstruction, onChangeSchema,
  // refine
  refinable, refineText, onRefineTextChange, onRefineField, refining, diff, onDismissDiff,
}) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [histOpen, setHistOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState(false);
  const [instructionText, setInstructionText] = useState(field.instruction || "");
  const [editingRules, setEditingRules] = useState(false);
  const [rulesText, setRulesText] = useState(field.output_rules || "");

  const tm = fieldTypeMeta(field.data_type);
  const conf = confidence || effectiveConfidence(field);
  const cs = CONFIDENCE_STYLES[conf] || confidenceStyle(field);
  const hasSample = !!context?.has_sample;
  const enabled = checkbox ? checked : field.enabled !== false;
  const editable = !!onRename;

  const val = context?.extraction_results?.[field.field_key];
  const hasResults = context?.extraction_results && Object.keys(context.extraction_results).length > 0;
  const showWarn = hasResults && isEmptyValue(val);
  const sampleDisplay = sample !== undefined ? sample : (
    field.data_type === "table"
      ? (Array.isArray(val) ? `${val.length} rows extracted` : "no rows extracted")
      : (isEmptyValue(val) ? "" : (typeof val === "object" ? JSON.stringify(val) : String(val)))
  );

  const [name, setName] = useState(field.field_key);
  const commitName = () => {
    const next = name.trim();
    if (next && next !== field.field_key) onRename(field.field_key, next);
    else setName(field.field_key);
  };

  const openComposer = () => { setOpen(true); setComposerOpen(true); };
  const submitRefine = () => { setComposerOpen(false); onRefineField(field.field_key); };

  const readOnlyBox = "w-full px-3 py-2.5 border border-[#ECE9EE] rounded-[9px] text-[13px] leading-[1.55] text-[#52525B] bg-[#FAF9FB] whitespace-pre-wrap";

  return (
    <div className={`border-b border-[#ECE9EE] last:border-b-0 ${enabled ? "bg-white" : "bg-[#FAF9FB]"}`}>
      {/* Collapsed header */}
      <div className="flex items-center justify-between gap-2.5 px-[22px] py-4 cursor-pointer" onClick={() => setOpen((o) => !o)}>
        <div className="flex items-center gap-2.5 min-w-0">
          {checkbox ? (
            <input type="checkbox" checked={checked} onClick={(e) => e.stopPropagation()} onChange={onToggle}
              className="w-4 h-4 flex-shrink-0 accent-[#A04A97]" />
          ) : onToggleEnable ? (
            <input type="checkbox" checked={enabled} onClick={(e) => e.stopPropagation()}
              onChange={(e) => onToggleEnable(field.field_key, e.target.checked)}
              className="w-4 h-4 flex-shrink-0 accent-[#A04A97]" />
          ) : null}
          <span className="w-6 h-6 rounded-[7px] bg-[#F5F5F5] text-[#52525B] text-[12px] font-semibold flex items-center justify-center flex-shrink-0">{index + 1}</span>
          {open ? <ChevronDown size={15} className="text-[#8A8A90] flex-shrink-0" /> : <ChevronRight size={15} className="text-[#8A8A90] flex-shrink-0" />}
          <span className={`font-mono text-[14px] font-semibold truncate ${enabled ? "text-[#A04A97]" : "text-[#8A8A90]"}`}>{field.field_key}</span>
          <span className={`${SX.badge} flex-shrink-0 ${tm.badge}`}>{tm.glyph} {String(field.data_type).toUpperCase()}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {diff && (
            <button onClick={(e) => { e.stopPropagation(); setReviewOpen(true); }}
              title="Review what AI changed"
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F7EBF5] text-[#893F82] text-[10px] font-semibold tracking-[0.03em]">
              <span className="w-[5px] h-[5px] rounded-full bg-[#A04A97]" /> AI REFINED
            </button>
          )}
          {showWarn && <AlertTriangle size={13} className="text-[#C77A1C]" title="Empty on last test" />}
          {onLocate && <span title={CONFIDENCE_STYLES[conf]?.tip} className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.03em] cursor-help ${cs.badge}`}>{cs.label}</span>}
          {(onDelete || refinable) && (
            <div className="relative flex" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setMenuOpen((o) => !o)} title="More"
                className={`w-7 h-7 rounded-[7px] flex items-center justify-center transition-colors ${menuOpen ? "bg-[#F5F5F5]" : ""} text-[#52525B] hover:bg-[#F5F5F5]`}>
                <MoreVertical size={16} />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-[90]" onClick={() => setMenuOpen(false)} />
                  <div className={`absolute top-[calc(100%+4px)] right-0 min-w-[176px] p-1.5 z-[91] ${SX.popover}`}>
                    {refinable && (
                      <div onClick={() => { setMenuOpen(false); openComposer(); }}
                        className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg cursor-pointer text-[13px] font-medium text-[#893F82] hover:bg-[#F7EBF5]">
                        <Sparkles size={15} /> Refine with AI
                      </div>
                    )}
                    {onDelete && (
                      <div onClick={() => { setMenuOpen(false); onDelete(field.field_key); }}
                        className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg cursor-pointer text-[13px] font-medium text-[#B33A1E] hover:bg-[#FCEDED]">
                        <Trash2 size={15} /> Delete field
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded body */}
      {open && (
        <div className="px-[22px] pb-6 pt-1">
          {refining ? (
            <div className="pt-5 pb-2">
              <div className="flex items-center gap-2.5 mb-4">
                <Spinner size={18} />
                <span className="text-[13.5px] font-semibold text-[#893F82]">Refining “{field.field_key}” with AI…</span>
              </div>
              <div className="relative h-1 rounded-full bg-[#F7EBF5] overflow-hidden">
                <div className="absolute inset-y-0 w-[42%] rounded-full bg-[#A04A97] animate-pulse" />
              </div>
              <div className="mt-4 flex flex-col gap-2.5">
                {["Reading the field and its context", "Tightening the extraction instruction", "Re-testing against your sample"].map((t) => (
                  <span key={t} className="flex items-center gap-2 text-[12px] text-[#8A8A90]">
                    <span className="w-1 h-1 rounded-full bg-[#E9D2E6]" />{t}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <>
              {composerOpen && (
                <div className="px-4 py-3.5 rounded-[11px] bg-[#FBF5FA] border border-[#E9D2E6] mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={15} className="text-[#893F82]" />
                    <span className="text-[12.5px] font-semibold text-[#893F82]">Refine with AI</span>
                  </div>
                  <p className="m-0 mb-2.5 text-[12px] text-[#52525B] leading-[1.5]">
                    Tell the AI what's wrong or what to change — it rewrites the instruction and re-tests against your sample.
                  </p>
                  <textarea value={refineText || ""} rows={2} autoFocus
                    onChange={(e) => onRefineTextChange(field.field_key, e.target.value)}
                    placeholder={'e.g. "This is grabbing the subtotal — capture the grand total instead"'}
                    className="w-full px-3 py-2.5 border-[1.5px] border-[#E9D2E6] rounded-[9px] text-[12.5px] text-[#1A1A1A] bg-white resize-y leading-[1.5] outline-none focus:border-[#A04A97]" />
                  <div className="flex items-center justify-end gap-2.5 mt-3">
                    <button onClick={() => { setComposerOpen(false); onRefineTextChange(field.field_key, ""); }}
                      className={`${SX.btn} ${SX.btnGhost} !py-2`}>Cancel</button>
                    <button onClick={submitRefine} disabled={!hasSample || !(refineText || "").trim()}
                      className={`${SX.btn} ${SX.btnPrimary} !py-2`}
                      title={!hasSample ? "Attach a sample in the Test screen first" : undefined}>
                      <Sparkles size={14} /> Refine field
                    </button>
                  </div>
                  {!hasSample && (
                    <div className="mt-2 text-[12px] text-[#C77A1C] flex gap-1.5">
                      <AlertTriangle size={12} className="flex-shrink-0 mt-px" /> Refine needs a sample — run a test first.
                    </div>
                  )}
                </div>
              )}

              {diff && (
                <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-[10px] bg-[#F7EBF5] border border-[#E9D2E6] mb-5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Sparkles size={14} className="text-[#893F82] flex-shrink-0" />
                    <span className="text-[12.5px] font-semibold text-[#893F82] truncate">AI refined this field.</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setReviewOpen(true)} className="px-3 py-1.5 rounded-lg bg-[#A04A97] text-white text-[12px] font-semibold">View changes</button>
                    {onDismissDiff && (
                      <button onClick={() => onDismissDiff(field.field_key)} title="Dismiss" className="w-6 h-6 rounded-md text-[#893F82] hover:bg-white/60 flex items-center justify-center"><X size={13} /></button>
                    )}
                  </div>
                </div>
              )}

              {/* Config */}
              <GroupLabel icon={Settings}>Configuration</GroupLabel>
              <div className="grid grid-cols-2 gap-5 mb-6">
                <div>
                  <Label>Field Name</Label>
                  {editable ? (
                    <input value={name} onChange={(e) => setName(e.target.value)} onBlur={commitName}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className={`${SX.input} ${SX.inputMono}`} />
                  ) : (
                    <div className={`${readOnlyBox} font-mono`}>{field.field_key}</div>
                  )}
                </div>
                <div>
                  <Label>Data Type</Label>
                  {editable ? (
                    <select value={field.data_type} onChange={(e) => onChangeType(field.field_key, e.target.value)} className={`${SX.input} cursor-pointer`}>
                      {SCHEMA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : (
                    <div className={readOnlyBox}>{field.data_type}</div>
                  )}
                </div>
              </div>

              {/* Schema — nested property tree for object/table fields */}
              {(field.data_type === "object" || field.data_type === "table") && onChangeSchema && (
                <>
                  <GroupLabel icon={Braces}>
                    {field.data_type === "table" ? "Row Schema" : "Object Schema"}
                  </GroupLabel>
                  <div className="mb-6">
                    <SchemaBuilder
                      dataType={field.data_type}
                      schema={field.schema_definition}
                      onChange={(schema) => onChangeSchema(field.field_key, schema)}
                    />
                  </div>
                </>
              )}

              {/* Instruction — editable directly or via Refine with AI */}
              <GroupLabel icon={Sparkles}>AI Instructions</GroupLabel>
              <Label hint={refinable ? "Tell the AI what to look for — edit directly or use Refine with AI" : "Tell the AI what to look for"}>Extraction Instruction</Label>
              {editingInstruction && onChangeInstruction ? (
                <div className="mb-4">
                  <textarea
                    autoFocus
                    value={instructionText}
                    onChange={(e) => {
                      setInstructionText(e.target.value);
                      onChangeInstruction(field.field_key, e.target.value);
                    }}
                    onBlur={() => setEditingInstruction(false)}
                    className="w-full px-3 py-2.5 border-[1.5px] border-[#A04A97] rounded-[9px] text-[13px] text-[#1A1A1A] bg-white resize-y leading-[1.5] outline-none focus:shadow-[0_0_0_3px_rgba(160,74,151,0.14)]"
                    rows={3}
                  />
                </div>
              ) : (
                <div className={`${readOnlyBox} min-h-[2.75rem] mb-4 cursor-pointer hover:bg-[#F5F5F5] transition-colors`}
                  onClick={() => onChangeInstruction && setEditingInstruction(true)}>
                  {field.instruction ? (
                    <span>{field.instruction}</span>
                  ) : (
                    <span className="text-[#8A8A90] italic">No instruction yet — click to add one</span>
                  )}
                </div>
              )}
              <Label hint="Formatting constraints">Output Rules</Label>
              {editingRules && onChangeInstruction ? (
                <div className="mb-4">
                  <textarea
                    autoFocus
                    value={rulesText}
                    onChange={(e) => { setRulesText(e.target.value); onChangeInstruction(field.field_key, e.target.value, true); }}
                    onBlur={() => setEditingRules(false)}
                    className="w-full px-3 py-2.5 border-[1.5px] border-[#A04A97] rounded-[9px] text-[13px] text-[#1A1A1A] bg-white resize-y leading-[1.5] outline-none focus:shadow-[0_0_0_3px_rgba(160,74,151,0.14)]"
                    rows={2}
                  />
                </div>
              ) : (
                <div className={`${readOnlyBox} min-h-[2.25rem] mb-4 cursor-pointer hover:bg-[#F5F5F5] transition-colors`}
                  onClick={() => onChangeInstruction && setEditingRules(true)}>
                  {field.output_rules ? (
                    <span>{field.output_rules}</span>
                  ) : (
                    <span className="text-[#8A8A90] italic">No rules yet — click to add formatting constraints</span>
                  )}
                </div>
              )}

              {/* Sample value + Locate */}
              <div className="flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <Label hint={confidence ? "Extracted from your sample" : undefined}>Sample Value</Label>
                  <div className={`font-mono text-[12.5px] max-w-[320px] whitespace-pre-wrap break-words ${sampleDisplay ? "text-[#1A1A1A]" : "text-[#8A8A90]"}`}>
                    {sampleDisplay || "No value found in sample"}
                  </div>
                </div>
                {onLocate && (
                  <button
                    className={`${SX.btn} flex-shrink-0 ${hasSample ? SX.btnSoft : "border border-[#ECE9EE] bg-[#F5F5F5] text-[#8A8A90] cursor-not-allowed"}`}
                    disabled={!hasSample} onClick={() => onLocate(field.field_key)}
                    title={hasSample ? "Find this value in the PDF" : "Run a test first"}
                  >
                    <MapPin size={13} /> Locate in PDF
                  </button>
                )}
              </div>

              {/* Instruction history */}
              {field.__history && field.__history.length > 1 && (
                <div className="mt-5 pt-4 border-t border-[#ECE9EE]">
                  <button className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#52525B] hover:text-[#1A1A1A]" onClick={() => setHistOpen((o) => !o)}>
                    {histOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Instruction history ({field.__history.length})
                  </button>
                  {histOpen && (
                    <div className="mt-2 flex flex-col gap-2">
                      {field.__history.slice().reverse().map((h, i) => (
                        <div key={i} className="px-3 py-2 rounded-[9px] bg-[#FAF9FB] border border-[#ECE9EE]">
                          <div className="flex items-center gap-1.5 mb-1">
I don't know.               <span className="text-[12px] font-semibold text-[#1A1A1A]">v{h.v}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${SOURCE_BADGE[h.source] || SOURCE_BADGE.saved}`}>{h.source.replace(/_/g, " ")}</span>
                            {i === 0 && <span className="text-[10px] text-[#2F7D44] font-semibold">• current</span>}
                          </div>
                          <div className="text-[12.5px] text-[#52525B] leading-[1.5]">{h.instruction || <span className="text-[#8A8A90] italic">(empty)</span>}</div>
                          {h.note && <div className="text-[11.5px] text-[#8A8A90] italic mt-1">{h.note}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Refinement review modal */}
      {reviewOpen && diff && (
        <div onClick={() => setReviewOpen(false)} className="fixed inset-0 bg-[rgba(20,10,24,0.34)] z-[130] flex items-center justify-center p-6">
          <div onClick={(e) => e.stopPropagation()} className="w-[min(620px,94vw)] max-h-[86vh] flex flex-col bg-white rounded-[16px] overflow-hidden shadow-[-2px_4px_20px_6px_rgba(0,0,0,0.10)]">
            <div className="flex items-center justify-between gap-3 px-[22px] py-4.5 border-b border-[#ECE9EE]">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-[30px] h-[30px] rounded-lg bg-[#F7EBF5] flex items-center justify-center flex-shrink-0"><Sparkles size={16} className="text-[#893F82]" /></span>
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-[#1A1A1A]">AI refinement</div>
                  <div className="text-[12px] text-[#8A8A90] font-mono truncate">{field.field_key}</div>
                </div>
              </div>
              <button onClick={() => setReviewOpen(false)} className="w-[30px] h-[30px] rounded-lg text-[#8A8A90] hover:bg-[#F5F5F5] flex items-center justify-center flex-shrink-0"><X size={15} /></button>
            </div>
            <div className="px-[22px] py-5 overflow-auto">
              <DiffRow label="Extraction Instruction" before={diff.before} after={diff.after} />
              {diff.feedback && (
                <div className="text-[12px] text-[#8A8A90] leading-[1.5]">
                  <span className="font-semibold text-[#52525B]">Your prompt:</span> “{diff.feedback}”
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2.5 px-[22px] py-3.5 border-t border-[#ECE9EE] bg-[#FAF9FB]">
              <button onClick={() => { setReviewOpen(false); if (onDismissDiff) onDismissDiff(field.field_key); }} className={`${SX.btn} ${SX.btnPrimary}`}>
                <Check size={14} /> Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldEditor;
